import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
  theme?: 'default' | 'admin';
  itemName?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  limit,
  total,
  onPageChange,
  className = '',
  theme = 'default',
  itemName = 'entries',
}) => {
  const totalPages = Math.ceil(total / limit);

  if (total === 0) return null;

  const startEntry = ((page - 1) * limit) + 1;
  const endEntry = Math.min(page * limit, total);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const pages = getPageNumbers();

  if (theme === 'admin') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: 'var(--admin-card-bg)',
        border: '1px solid var(--admin-card-border)',
        borderRadius: '0',
        fontFamily: "'Inter', sans-serif",
        fontSize: '12px'
      }} className={className}>
        <span style={{ color: 'var(--admin-card-subtext)' }}>
          Showing <strong style={{ color: 'var(--admin-card-text)' }}>{startEntry}</strong> to <strong style={{ color: 'var(--admin-card-text)' }}>{endEntry}</strong> of <strong style={{ color: 'var(--admin-card-text)' }}>{total}</strong> {itemName}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            type="button"
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
            style={{
              padding: '6px 10px',
              borderRadius: '0',
              border: '1px solid var(--admin-card-border)',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              color: page === 1 ? 'var(--admin-card-subtext)' : 'var(--admin-card-text)',
              fontSize: '11px',
              fontWeight: 600,
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              opacity: page === 1 ? 0.5 : 1
            }}
          >
            <ChevronLeft size={14} /> Previous
          </button>

          {pages.map((p, index) => {
            if (p === '...') {
              return (
                <span key={`ellipsis-${index}`} style={{
                  width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--admin-card-subtext)', fontWeight: 700, letterSpacing: '2px'
                }}>...</span>
              );
            }
            const isActive = p === page;
            return (
              <button
                key={index}
                type="button"
                onClick={() => onPageChange(p as number)}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '0',
                  border: isActive ? 'none' : '1px solid var(--admin-card-border)',
                  backgroundColor: isActive ? '#ffffff' : 'transparent',
                  color: isActive ? '#000000' : 'var(--admin-card-text)',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {p}
              </button>
            );
          })}

          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
            style={{
              padding: '6px 10px',
              borderRadius: '0',
              border: '1px solid var(--admin-card-border)',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              color: page === totalPages ? 'var(--admin-card-subtext)' : 'var(--admin-card-text)',
              fontSize: '11px',
              fontWeight: 600,
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              opacity: page === totalPages ? 0.5 : 1
            }}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 mt-4 ${className}`}>
      {/* Left side: showing entries summary */}
      <p className="text-sm text-slate-500 font-medium">
        Showing <span className="font-semibold text-slate-900">{startEntry}</span> to{' '}
        <span className="font-semibold text-slate-900">{endEntry}</span> of{' '}
        <span className="font-semibold text-slate-900">{total}</span> {itemName}
      </p>

      {/* Right side: page controls */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1.5 p-1.5 bg-slate-50 border border-slate-200/80 rounded-2xl shadow-sm">
          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            className="w-9 h-9 rounded-xl text-slate-600 hover:bg-white hover:text-slate-900 flex items-center justify-center disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all duration-200 hover:shadow-sm"
            title="Previous Page"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex items-center gap-1">
            {pages.map((p, index) => (
              p === '...' ? (
                <span key={`ellipsis-${index}`} className="w-9 h-9 flex items-center justify-center text-slate-500 font-semibold tracking-widest">...</span>
              ) : (
                <button
                  key={index}
                  onClick={() => onPageChange(p as number)}
                  className={`w-9 h-9 rounded-xl text-sm font-semibold flex items-center justify-center transition-all duration-200 ${page === p
                      ? 'bg-[#031828] text-white shadow-md font-bold'
                      : 'text-slate-600 hover:bg-white hover:shadow-sm hover:text-slate-900'
                    }`}
                >
                  {p}
                </button>
              )
            ))}
          </div>

          <button
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="w-9 h-9 rounded-xl text-slate-600 hover:bg-white hover:text-slate-900 flex items-center justify-center disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all duration-200 hover:shadow-sm"
            title="Next Page"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

